/*
 * This class is copied from Backstage 1.39.1.
 * CHANGES:
 * - added a simpleProvider in signInProviders.
 * - in getSignInProviders added an if to load simple providers.
 */

import {
  errorApiRef,
  IdentityApi,
  SignInPageProps,
  useApi,
  useApiHolder,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { ForwardedError } from '@backstage/errors';
import { useMountEffect } from '@react-hookz/web';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { coreComponentsTranslationRef } from '../translation';
import { commonProvider } from './commonProvider';
import { customProvider } from './customProvider';
import { guestProvider } from './guestProvider';
import { IdentityApiSignOutProxy } from './IdentityApiSignOutProxy';
import { simpleProvider } from './simpleProvider';
import {
  IdentityProviders,
  SignInProvider,
  SignInProviderConfig,
} from './types';
import lastSeenOnlineStore from '../../components/AutoLogoutProvider/lastSeenOnlineStore';

const PROVIDER_STORAGE_KEY = '@backstage/core:SignInPage:provider';

export type SignInProviderType = {
  [key: string]: {
    components: SignInProvider;
    id: string;
    config?: SignInProviderConfig;
  };
};

const signInProviders: { [key: string]: SignInProvider } = {
  guest: guestProvider,
  custom: customProvider,
  common: commonProvider,
  simple: simpleProvider,
};

function validateIDs(id: string, providers: SignInProviderType): void {
  if (id in providers)
    throw new Error(
      `"${id}" ID is duplicated. IDs of identity providers have to be unique.`,
    );
}

export function getSignInProviders(
  identityProviders: IdentityProviders,
): SignInProviderType {
  const providers = identityProviders.reduce(
    (acc: SignInProviderType, config) => {
      if (typeof config === 'string') {
        validateIDs(config, acc);
        acc[config] = { components: signInProviders[config], id: config };

        return acc;
      }

      const { id } = config as SignInProviderConfig;
      validateIDs(id, acc);

      if (config.id.startsWith('simple')) {
        acc[id] = { components: signInProviders.simple, id, config };
      } else {
        acc[id] = { components: signInProviders.common, id, config };
      }
      return acc;
    },
    {},
  );

  return providers;
}

export const useSignInProviders = (
  providers: SignInProviderType,
  onSignInSuccess: SignInPageProps['onSignInSuccess'],
) => {
  const errorApi = useApi(errorApiRef);
  const apiHolder = useApiHolder();
  const [loading, setLoading] = useState(true);

  const { t } = useTranslationRef(coreComponentsTranslationRef);
  // User was redirected back to sign in page with error from auth redirect flow
  const [searchParams, _setSearchParams] = useSearchParams();

  useMountEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      errorApi.post(
        new ForwardedError(t('signIn.loginFailed'), new Error(errorParam)),
      );
    }
  });

  // This decorates the result with sign out logic from this hook
  const handleWrappedResult = useCallback(
    (identityApi: IdentityApi) => {
      onSignInSuccess(
        IdentityApiSignOutProxy.from({
          identityApi,
          signOut: async () => {
            localStorage.removeItem(PROVIDER_STORAGE_KEY);
            await identityApi.signOut?.();
          },
        }),
      );
    },
    [onSignInSuccess],
  );

  // In this effect we check if the user has already selected an existing login
  // provider, and in that case try to load an existing session for the provider.
  useLayoutEffect(() => {
    if (!loading) {
      return undefined;
    }

    // We can't use storageApi here, as it might have a dependency on the IdentityApi
    const selectedProviderId = localStorage.getItem(
      PROVIDER_STORAGE_KEY,
    ) as string;

    // No provider selected, let the user pick one
    if (selectedProviderId === null) {
      setLoading(false);
      return undefined;
    }

    const provider = providers[selectedProviderId];
    if (!provider) {
      setLoading(false);
      return undefined;
    }

    let didCancel = false;

    provider.components
      .loader(apiHolder, provider.config?.apiRef!)
      .then(result => {
        if (didCancel) {
          localStorage.removeItem(PROVIDER_STORAGE_KEY);
          return;
        }
        if (result) {
          handleWrappedResult(result);
        } else {
          setLoading(false);
        }
      })
      .catch(error => {
        localStorage.removeItem(PROVIDER_STORAGE_KEY);
        if (didCancel) {
          return;
        }
        errorApi.post(error);
        setLoading(false);
      });

    return () => {
      didCancel = true;
    };
  }, [
    loading,
    errorApi,
    onSignInSuccess,
    apiHolder,
    providers,
    handleWrappedResult,
  ]);

  // This renders all available sign-in providers
  const elements = useMemo(
    () =>
      Object.keys(providers).map(key => {
        const provider = providers[key];

        const { Component } = provider.components;

        const handleSignInSuccess = (result: IdentityApi) => {
          handleWrappedResult(result);
        };

        const handleSignInStarted = () => {
          // Clear (reset) the previous stored last seen online timestamp for the user (used by auto-logout)
          lastSeenOnlineStore().delete();

          localStorage.setItem(
            PROVIDER_STORAGE_KEY,
            provider?.config?.id || provider.id,
          );
        };

        const handleSignInFailure = () => {
          localStorage.removeItem(PROVIDER_STORAGE_KEY);
        };

        return (
          <Component
            key={provider.id}
            config={provider.config!}
            onSignInStarted={handleSignInStarted}
            onSignInSuccess={handleSignInSuccess}
            onSignInFailure={handleSignInFailure}
          />
        );
      }),
    [providers, handleWrappedResult],
  );

  return [loading, elements];
};
